/**
 * 统一 WebSocket 客户端
 *
 * 技术选型：SockJS + STOMP（与后端约定一致）
 * 依赖：sockjs-client @stomp/stompjs
 *
 * 支持业务：
 *   - 危机预警实时通知  (command: CRISIS_EVENT)
 *   - 在线咨询消息      (command: RECEIVE_MSG / MSG_ACK)
 *   - 通用通知          (command: SESSION_CREATED / SESSION_STATUS / HISTORY_AUTH_RESPONSE)
 *   - 转人工服务通知    (command: HUMAN_SERVICE_NEW_REQUEST / HUMAN_SERVICE_ASSIGNED)
 *
 * 用法示例（在 setup 中）：
 *   import { useWsClient } from '@/utils/useWsClient';
 *   const { on, off, publish, connected } = useWsClient();
 *   on('CRISIS_EVENT', handler);
 *   onBeforeUnmount(() => off('CRISIS_EVENT', handler));
 */

import { ref } from 'vue';
import { Modal, message, notification } from 'ant-design-vue';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import basicStorage from '@/utils/basicStorage';
import { rotateAuthSessionEpoch } from '@/utils/authSession';

// ─── 单例状态 ────────────────────────────────────────────────────────────────
let stompClient = null;
const connected = ref(false);

// 消息处理器注册表：{ [command]: Set<handler> }
const handlers = {};

// ─── 工具函数 ────────────────────────────────────────────────────────────────
const getToken = () => basicStorage.getItem('token') || '';

const getHttpBaseUrl = () => {
  if (import.meta.env.PROD) {
    return `${location.protocol}//${location.host}`;
  }
  return 'http://127.0.0.1:11001';
};

const dispatch = data => {
  const cmd = data?.command;
  if (!cmd) return;
  const set = handlers[cmd];
  if (set && set.size > 0) {
    set.forEach(fn => {
      try {
        fn(data);
      } catch (e) {
        console.error('[WsClient] handler error', e);
      }
    });
  }
};

// ─── 核心连接逻辑 ────────────────────────────────────────────────────────────
const connect = () => {
  if (stompClient?.active) return;

  const token = getToken();
  if (!token) {
    console.warn('[WsClient] 无 token，跳过 WebSocket 连接');
    return;
  }

  const baseUrl = getHttpBaseUrl();
  let subscriptionGeneration = 0;
  const client = new Client({
    webSocketFactory: () => new SockJS(`${baseUrl}/ws/consultation`),
    connectHeaders: { Authorization: token, terminal: 'pc' },
    reconnectDelay: 3000,

    onConnect: () => {
      if (stompClient !== client) {
        client.deactivate();
        return;
      }
      const generation = ++subscriptionGeneration;
      const pendingReceipts = new Set();
      const receiptPrefix = `pc-sub-${Date.now()}-${generation}`;
      let readyTimer = null;
      let ready = false;
      const markReady = () => {
        if (
          ready ||
          generation !== subscriptionGeneration ||
          stompClient !== client ||
          !client.connected
        ) {
          return;
        }
        ready = true;
        if (readyTimer) clearTimeout(readyTimer);
        connected.value = true;
        console.info('[WsClient] 连接成功');
      };
      const receiptHeaders = name => {
        const receipt = `${receiptPrefix}-${name}`;
        pendingReceipts.add(receipt);
        client.watchForReceipt(receipt, () => {
          if (generation !== subscriptionGeneration || stompClient !== client) return;
          pendingReceipts.delete(receipt);
          if (pendingReceipts.size === 0) markReady();
        });
        return { receipt };
      };

      // 订阅个人消息队列（咨询消息 / 危机预警通知）
      client.subscribe('/user/queue/messages', msg => {
        if (stompClient !== client) return;
        try {
          dispatch(JSON.parse(msg.body));
        } catch (e) {
          console.error('[WsClient] 消息解析失败', e);
        }
      }, receiptHeaders('messages'));

      // 订阅通用通知队列（会话状态、授权响应、会话创建等）
      client.subscribe('/user/queue/notifications', msg => {
        if (stompClient !== client) return;
        try {
          dispatch(JSON.parse(msg.body));
        } catch (e) {
          console.error('[WsClient] 通知消息解析失败', e);
        }
      }, receiptHeaders('notifications'));

      // 订阅危机预警专用队列
      client.subscribe('/user/queue/crisis', msg => {
        if (stompClient !== client) return;
        try {
          dispatch(JSON.parse(msg.body));
        } catch (e) {
          console.error('[WsClient] 危机消息解析失败', e);
        }
      }, receiptHeaders('crisis'));

      // 优先等待 broker 确认订阅；不支持 RECEIPT 的 broker 使用短暂兜底后开始补拉。
      if (!ready) readyTimer = setTimeout(markReady, 1500);
    },

    onDisconnect: () => {
      if (stompClient === client) {
        subscriptionGeneration += 1;
        connected.value = false;
      }
      console.info('[WsClient] 连接断开');
    },

    onStompError: frame => {
      if (stompClient === client) {
        subscriptionGeneration += 1;
        connected.value = false;
      }
      console.error('[WsClient] STOMP 错误', frame);
    },

    onWebSocketClose: () => {
      if (stompClient === client) {
        subscriptionGeneration += 1;
        connected.value = false;
      }
      if (stompClient === client && client.active) {
        client.connectHeaders = {
          Authorization: getToken(),
          terminal: 'pc',
        };
      }
    },
  });

  stompClient = client;
  client.activate();
};

const disconnect = async ({ clearHandlers = false } = {}) => {
  const client = stompClient;
  stompClient = null;
  connected.value = false;
  stopHeartbeat();
  if (clearHandlers) {
    Object.values(handlers).forEach(commandHandlers => commandHandlers.clear());
  }
  try {
    await client?.deactivate();
  } catch (error) {
    console.warn('[WsClient] 断开连接失败', error);
  }
};

export const resetWsClient = () => disconnect({ clearHandlers: true });

export const resetRealtimeSession = async () => {
  rotateAuthSessionEpoch();
  // 静态弹窗不会随布局卸载自动销毁，账号切换时必须同步清理旧账号上下文。
  message.destroy();
  notification.destroy();
  Modal.destroyAll();
  const disconnectPromise = resetWsClient();
  try {
    const [{ humanServiceReceptionStore }, { consultationSessionStatusStore }] =
      await Promise.all([
        import('@/store/humanServiceReception'),
        import('@/store/consultationSessionStatus'),
      ]);
    humanServiceReceptionStore().resetReceptionState();
    consultationSessionStatusStore().$reset();
  } catch (error) {
    console.warn('[WsClient] 实时会话状态重置失败', error);
  }
  await disconnectPromise;
};

// ─── 心跳（30s 一次）────────────────────────────────────────────────────────
let heartbeatTimer = null;

const startHeartbeat = () => {
  stopHeartbeat();
  heartbeatTimer = setInterval(() => {
    if (stompClient?.connected) {
      stompClient.publish({
        destination: '/app/consultation/heartbeat',
        body: JSON.stringify({ command: 'HEARTBEAT', timestamp: Date.now() }),
      });
    }
  }, 30000);
};

const stopHeartbeat = () => {
  clearInterval(heartbeatTimer);
  heartbeatTimer = null;
};

// ─── 公开 API ─────────────────────────────────────────────────────────────────
/**
 * 注册消息处理器
 * @param {string} command  消息 command 字段值
 * @param {Function} fn     处理函数
 */
const on = (command, fn) => {
  if (!handlers[command]) handlers[command] = new Set();
  handlers[command].add(fn);
};

/**
 * 注销消息处理器
 * @param {string} command
 * @param {Function} fn
 */
const off = (command, fn) => {
  handlers[command]?.delete(fn);
};

/**
 * 发布消息到服务端
 * @param {string} destination  STOMP destination，如 '/app/consultation/send'
 * @param {object} body         消息体对象
 */
const publish = (destination, body) => {
  if (!stompClient?.connected) {
    console.warn('[WsClient] 未连接，消息发送失败');
    return false;
  }
  stompClient.publish({ destination, body: JSON.stringify({ ...body, timestamp: Date.now() }) });
  return true;
};

// ─── Composable ──────────────────────────────────────────────────────────────
/**
 * 获取统一 WebSocket 客户端实例（单例）。
 * 首次调用时建立连接并启动心跳；后续调用复用已有连接。
 */
export const useWsClient = () => {
  if (!stompClient?.active) {
    connect();
    startHeartbeat();
  }
  return { on, off, publish, connect, disconnect, connected };
};

export default useWsClient;
